import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { StockSchema, StockCreateInputSchema, StockUpdateInputSchema, type Stock, type StockCreateInput, type StockUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `stock` (quantité d'un article à un emplacement, org-wide).
 *
 * Réplique le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`StockCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit jamais
 * un document pour découvrir ensuite qu'il est invalide.
 */

export type { StockCreateInput };

/** Nom de la collection Firestore de la famille stock. */
export const STOCKS_COLLECTION = 'stocks';

/**
 * Repository typé pour la famille `stock`.
 * Toute lecture est validée par `StockSchema`, toute écriture par
 * `StockCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface StockRepository {
  create(input: StockCreateInput): Promise<Stock>;
  update(id: string, patch: StockUpdateInput): Promise<Stock>;
  list(options?: ListOptions): Promise<Stock[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Stock | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Stock>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Stock>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Stock>;
}

const stockConverter = createConverter<Stock>((raw: DocumentData) => StockSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createStockRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): StockRepository {
  const col = collection(db, STOCKS_COLLECTION).withConverter(stockConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Stock>(db, col, STOCKS_COLLECTION, getActorId);

  return {
    async create(input: StockCreateInput): Promise<Stock> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = StockCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.quantityOnHand !== undefined) optional['quantityOnHand'] = fields.quantityOnHand;
      if (fields.quantityReserved !== undefined) optional['quantityReserved'] = fields.quantityReserved;
      if (fields.reorderPoint !== undefined) optional['reorderPoint'] = fields.reorderPoint;
      if (fields.unit !== undefined) optional['unit'] = fields.unit;
      if (fields.lotId !== undefined) optional['lotId'] = fields.lotId;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        productId: fields.productId,
        placeId: fields.placeId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Stock);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return StockSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: StockUpdateInput): Promise<Stock> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = StockUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, STOCKS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`stock ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Stock[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<Stock | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
